/**
 * Sidebar navigation: logo, main nav, divider, secondary nav.
 * Single list with same gap; divider only between Learning Center and Subscription.
 */

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Image } from "../../../../design-system";
import { colors } from "../../../../design-system/colors";
import { HomeIcon, MyLibraryIcon, LearningCenterIcon } from "../../../../design-system/icons";
import {
  BRAND_NAME,
  BRAND_LOGO_ALT,
} from "../../../../design-system/constants/brand";
import logo from "../../../../../assets/brand/logo_site.svg";
import { NAV_ITEMS } from "../../constants/navigation";
import SidebarNavItem from "./NavItem";
import { ROUTES } from "../../../../../constants";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

// Adapt a MUI icon to the size/color prop shape the sidebar's design-system icons use.
const PricingIcon: React.FC<{ size?: number; color?: string }> = ({ size, color }) => (
  <WorkspacePremiumIcon sx={{ fontSize: size, color }} />
);

const SIDEBAR_WIDTH = 260;
const LOGO_SECTION_PADDING = "24px";
const SIDEBAR_ICON_SIZE = 20;
const NAV_LIST_GAP = "4px";

const SIDEBAR_ICONS: Record<
  string,
  React.FC<{ size?: number; color?: string }>
> = {
  Home: HomeIcon,
  Library: MyLibraryIcon,
  LearningCenter: LearningCenterIcon,
  Pricing: PricingIcon,
};

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(false);

  const currentPath = location.pathname.endsWith("/")
    ? location.pathname
    : `${location.pathname}/`;

  const isActive = (path: string) => {
    if (path === ROUTES.HOME) {
      return currentPath === ROUTES.HOME;
    }
    if (path === ROUTES.MY_LIBRARY) {
      return currentPath === path || currentPath.startsWith(ROUTES.MY_LIBRARY);
    }
    return currentPath === path;
  };

  const handleNavClick = (path: string, external?: boolean) => {
    if (external) return window.open(path, "_blank", "noopener,noreferrer");
    navigate(path);
  };

  const renderNavIcon = (iconKey: string, active: boolean) => {
    const IconComponent = SIDEBAR_ICONS[iconKey];
    if (!IconComponent) return null;
    return (
      <IconComponent
        size={SIDEBAR_ICON_SIZE}
        color={active ? colors.neutral.white : colors.neutral[700]}
      />
    );
  };

  const sidebarContent = (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          height: "70px",
          flexShrink: 0,
        }}
      >
        <Image
          src={logo}
          alt={BRAND_LOGO_ALT}
          containerProps={{
            sx: {
              height: "48px",
              width: "auto",
            },
          }}
          imageStyle={{
            height: "100%",
            width: "auto",
          }}
        />
        <Typography
          sx={{
            fontSize: "22px",
            fontWeight: 700,
            fontFamily: "Outfit, sans-serif",
            lineHeight: "27px",
          }}
        >
          <span style={{ color: "#e61d50" }}>Cherry</span>
          <span style={{ color: colors.neutral.white }}>Clipz</span>
          <span style={{ color: "#e61d50", fontFamily: "Impact, 'Arial Black', sans-serif", fontWeight: 900, fontSize: "22px", marginLeft: "10px", letterSpacing: "3px", textShadow: "1px 1px 0px #8b0000" }}>V2</span>
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: NAV_LIST_GAP,
          flex: 1,
        }}
      >
        {Object.values(NAV_ITEMS).map((item) => {
          const external = "external" in item && item.external;
          const active = external ? false : isActive(item.path);
          return (
            <SidebarNavItem
              key={item.path}
              label={item.label}
              isActive={active}
              onClick={() => {
                handleNavClick(item.path, external);
                if (isMobile) setOpen(false);
              }}
              icon={renderNavIcon(item.icon, active)}
            />
          );
        })}
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <>
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            top: "16px",
            left: "16px",
            zIndex: 1500,
            backgroundColor: colors.background[800],
            color: colors.neutral.white,
            "&:hover": {
              backgroundColor: colors.background[700],
            },
          }}
        >
          <MenuIcon />
        </IconButton>
        <Drawer
          variant="temporary"
          open={open}
          onClose={() => setOpen(false)}
          sx={{
            "& .MuiDrawer-paper": {
              width: "260px",
              backgroundColor: colors.background[900],
              padding: LOGO_SECTION_PADDING,
              gap: "8px",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      </>
    );
  }

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        top: 0,
        width: `${SIDEBAR_WIDTH}px`,
        height: "100vh",
        borderRadius: "0 16px 16px 0",
        display: "flex",
        flexDirection: "column",
        paddingTop: 0,
        paddingLeft: LOGO_SECTION_PADDING,
        paddingRight: LOGO_SECTION_PADDING,
        paddingBottom: LOGO_SECTION_PADDING,
        gap: "8px",
        zIndex: 2,
      }}
    >
      {sidebarContent}
    </Box>
  );
};

export default Sidebar;
